import { isBlankPlan, monthsToClose } from './demoRules.js'
import { seatsPerDayFor, staffingFillFor, staffingTargetsFor } from './demoStaffing.js'
import type { DemoSize } from '../types.js'
import { DEMO_BEHAVIOR_OVERRIDES, DEMO_FACILITIES, DEMO_SECOND_OPERATOR, DEMO_UNIT_RECORDS, DEMO_UNPRIORITIZED_FACILITY_ID, DEMO_WORKERS, type DemoFacility, type DemoUnitRecord, type DemoWorker } from './demoData.js'
import { LARGE_ACTIVE_EXTRA_FACILITIES, LARGE_OPERATOR_TOTAL, largeExtraFacilities, largeExtraOperators, largeExtraWorkers } from './demoLargeData.js'
import { TRAINING_FACILITY, TRAINING_WORKER_IDS, TRAINING_WORKER_LOGIN_ID, trainingWorkerNamed } from './trainingCast.js'

/**
 * How much fake data a demo client is set up with. "Standard" is the full
 * dataset: every facility in demoData.ts, the training cast, behaviors and
 * a second operator. "Minimum" is the smallest client worth showing: one
 * facility, three workers, their outcomes and a ranking - and nothing else
 * (behaviors stay at their defaults), so it sets up in a few calls.
 *
 * Either size also takes a span of months: how far back the reported shift
 * outcomes go, with every earlier month closed, so each worker's score
 * trend has a point per month. Which workers get the whole span, how far
 * back the others go and the shape of each worker's story come from a
 * seeded generator - varied across workers, identical for every demo
 * client, so a demo is predictable to give.
 *
 * "Blank" sets up nothing but the client itself - the registration, its
 * key and the operator login - for a demo built by hand afterwards: the
 * operator it just created adds the facilities and workers from the
 * dashboard (SeeWhoCares's admin adds neither). It is still a demo
 * client, so it can be deleted like the others.
 *
 * "Large" is the standard dataset at a large operator's scale: 316
 * facilities and ten operators (demoLargeData.ts). The facilities beyond
 * the standard ones each get an administrator and a small roster but no
 * activity, so the setup stays a matter of minutes.
 */

/**
 * The story a demo tells (demo history plan H4, D7): each a set of dials on the same history, so every one is as
 * realistic as the others. Steady is a well-run building; Short-staffed has a roster at about 70% of what the plan
 * needs, so the release waits and the board carries the gap; Winter surge has the census climbing over the last six
 * weeks; Census decline has it falling; Turnaround has most of the roster improving month on month; Struggling has
 * scores sliding and call-outs rising.
 */
export type DemoScenario = 'steady' | 'short_staffed' | 'winter_surge' | 'census_decline' | 'turnaround' | 'struggling'

export const DEMO_SCENARIO_OPTIONS: readonly { scenario: DemoScenario; label: string; summary: string }[] = [
  { scenario: 'steady', label: 'Steady', summary: 'A well-run building: about nine seats in ten filled from the roster, the board covering the rest, scores mostly holding.' },
  { scenario: 'short_staffed', label: 'Short-staffed, hiring', summary: 'The roster is about 70% of what the plan needs: open seats over the release limit, more run short, the workforce report saying who to hire.' },
  { scenario: 'winter_surge', label: 'Winter surge', summary: 'The census climbs over the last six weeks: the plan asks for more seats, rises are posted and the HPPD is held.' },
  { scenario: 'census_decline', label: 'Census decline', summary: 'Beds empty out over the last six weeks: the plan needs fewer seats, and flexes fall lowest tier first.' },
  { scenario: 'turnaround', label: 'Turnaround', summary: 'Most of the roster improving month on month: call-outs falling, tiers moving up, the summaries showing it.' },
  { scenario: 'struggling', label: 'Struggling', summary: 'Scores sliding: more call-outs and no-shows, more shifts run short, more notes from workers.' },
]
export const DEFAULT_DEMO_SCENARIO: DemoScenario = 'steady'
/** The scenarios a large demo deals its active facilities, in turn from the one chosen: one of each, then repeats (plan D5). */
const SCENARIO_ROTATION: readonly DemoScenario[] = DEMO_SCENARIO_OPTIONS.map((option) => option.scenario)

/**
 * What a custom demo is made of (the user's request, 25 October 2026): the admin sets each major part of the data
 * within limits that keep it realistic and inside what the history job and the one-call delete can carry.
 */
export interface CustomDemoSettings {
  /** Facilities with a full roster and history. */
  facilities: number
  /** Further facilities with a small roster and no activity, as a large operator has. */
  rosterOnlyFacilities: number
  unitsPerFacility: number
  bedsPerUnit: number
  /** The share of beds the census is planned at, as a percentage. */
  occupancyPct: number
  /** The share of what the staffing plan requires that the roster can staff, as a percentage: 90 is a steady building, below 80 a short-staffed one. */
  staffingPct: number
  /** Months published after this one: at least the two that may be taking bids. */
  monthsAhead: number
}

/** Each custom setting's range: realistic for a skilled nursing facility, and inside what a demo can be built and deleted with. */
export const CUSTOM_DEMO_LIMITS: Readonly<Record<keyof CustomDemoSettings, { min: number; max: number; label: string; unit?: string }>> = {
  facilities: { min: 1, max: 12, label: 'Facilities with history' },
  rosterOnlyFacilities: { min: 0, max: 300, label: 'Further facilities, roster only' },
  unitsPerFacility: { min: 1, max: 4, label: 'Units per facility' },
  bedsPerUnit: { min: 10, max: 60, label: 'Beds per unit' },
  occupancyPct: { min: 70, max: 100, label: 'Occupancy', unit: '%' },
  staffingPct: { min: 50, max: 120, label: 'Staffed from the roster', unit: '%' },
  monthsAhead: { min: 2, max: 4, label: 'Months published ahead' },
}

/** A sales login's custom demo stays within a standard demo's reach: the API refuses a sales login the large one. */
export const SALES_CUSTOM_LIMITS: Partial<Record<keyof CustomDemoSettings, { max: number }>> = { facilities: { max: 4 }, rosterOnlyFacilities: { max: 0 } }

/** Where the custom form starts: one standard building, steadily staffed, two months ahead. */
export const DEFAULT_CUSTOM_DEMO: CustomDemoSettings = { facilities: 1, rosterOnlyFacilities: 0, unitsPerFacility: 2, bedsPerUnit: 25, occupancyPct: 90, staffingPct: 90, monthsAhead: 2 }

/** A custom demo's history stops at a year once it has more facilities than a standard demo (as a large one does). */
const CUSTOM_LONG_HISTORY_FACILITIES = 4
/**
 * The most shifts a custom demo may publish: per facility, what one background run of its history writes well inside
 * its fifteen minutes; in all, what the demo's delete clears in its own. Every setting inside its range can still
 * multiply past these.
 */
export const CUSTOM_MAX_SHIFTS_PER_FACILITY = 50_000
export const CUSTOM_MAX_SHIFTS = 300_000
const DAYS_PER_MONTH = 30.4

/** The shifts a custom demo will publish: each facility's seats a day over the history and the months ahead. */
export function customShiftEstimate(settings: CustomDemoSettings, monthSpan: number): { perFacility: number; total: number } {
  const daily = seatsPerDayFor(customUnits(settings))
  const perFacility = Math.round((daily.RN + daily.LPN + daily.CNA) * DAYS_PER_MONTH * (monthSpan + settings.monthsAhead))
  return { perFacility, total: perFacility * settings.facilities }
}

/** What is wrong with a custom demo's settings, one sentence each; empty when every one is within its range. */
export function customDemoProblems(settings: CustomDemoSettings, monthSpan: number, forSales = false): string[] {
  const problems = (Object.keys(CUSTOM_DEMO_LIMITS) as (keyof CustomDemoSettings)[]).flatMap((key) => {
    const { min, label } = CUSTOM_DEMO_LIMITS[key]
    const max = forSales ? (SALES_CUSTOM_LIMITS[key]?.max ?? CUSTOM_DEMO_LIMITS[key].max) : CUSTOM_DEMO_LIMITS[key].max
    const value = settings[key]
    return Number.isInteger(value) && value >= min && value <= max ? [] : [`${label}: a whole number from ${min} to ${max}`]
  })
  const maxSpan = customMaxMonthSpan(settings)
  const spanProblems = monthSpan > maxSpan ? [`Months of history: at most ${maxSpan} with ${settings.facilities} facilities with history`] : []
  if (problems.length > 0 || spanProblems.length > 0 || !Number.isFinite(monthSpan)) {
    return [...problems, ...spanProblems]
  }
  const { perFacility, total } = customShiftEstimate(settings, monthSpan)
  const sizeProblems = [
    ...(perFacility > CUSTOM_MAX_SHIFTS_PER_FACILITY ? [`Too much for one facility's history: about ${perFacility.toLocaleString()} shifts, at most ${CUSTOM_MAX_SHIFTS_PER_FACILITY.toLocaleString()} - fewer units, beds or months`] : []),
    ...(total > CUSTOM_MAX_SHIFTS ? [`Too much for one demo: about ${total.toLocaleString()} shifts, at most ${CUSTOM_MAX_SHIFTS.toLocaleString()} - fewer facilities with history, units, beds or months`] : []),
  ]
  return sizeProblems
}

/** The longest history a custom demo may have. */
export function customMaxMonthSpan(settings: Pick<CustomDemoSettings, 'facilities'>): number {
  return settings.facilities > CUSTOM_LONG_HISTORY_FACILITIES ? LARGE_MONTH_SPAN_MAX : MONTH_SPAN_LIMITS.max
}

/** A custom facility's units: Unit A to D, each with the beds set and the census planned at the occupancy. */
export function customUnits(settings: Pick<CustomDemoSettings, 'unitsPerFacility' | 'bedsPerUnit' | 'occupancyPct'>): DemoUnitRecord[] {
  return Array.from({ length: settings.unitsPerFacility }, (_, index) => {
    const letter = String.fromCharCode(65 + index)
    return { id: `unit-${letter.toLowerCase()}`, name: `Unit ${letter}`, beds: settings.bedsPerUnit, plannedCensus: Math.round((settings.bedsPerUnit * settings.occupancyPct) / 100) }
  })
}

/** What the admin chooses on the form: the dataset, how many months of history it spans (the current month included), and its story. */
export interface DemoChoice {
  size: DemoSize
  monthSpan: number
  /** Left out, Steady. */
  scenario?: DemoScenario
  /** A custom demo's settings; left out, the defaults. */
  custom?: CustomDemoSettings
}

export const DEFAULT_DEMO_SIZE: DemoSize = 'minimum'

/**
 * Half a year by default (demo history plan D1, 25 October 2026): enough months behind this one for the Month tab,
 * the score trends and the reports to look like a facility that has been running. The history is written on the
 * API in the background, so a longer span costs minutes, not partner calls.
 */
export const DEFAULT_MONTH_SPAN = 6
/** The score history keeps 24 finals, so a longer span would not all be visible. */
export const MONTH_SPAN_LIMITS = { min: 1, max: 24 } as const
/** A large demo's history stops at a year (plan D1): its active facilities are many more months to write. */
export const LARGE_MONTH_SPAN_MAX = 12

/** The most months of history a size may have. */
export function maxMonthSpanFor(size: DemoSize): number {
  return size === 'large' ? LARGE_MONTH_SPAN_MAX : MONTH_SPAN_LIMITS.max
}

export function isValidMonthSpan(value: number, size: DemoSize | null = null): boolean {
  return Number.isInteger(value) && value >= MONTH_SPAN_LIMITS.min && value <= (size ? maxMonthSpanFor(size) : MONTH_SPAN_LIMITS.max)
}

/** The facility a minimum demo keeps, and how much of its roster: the first three workers are a gold, a silver and a bronze. */
const MINIMUM_FACILITY_ID = 'north-campus'
const MINIMUM_WORKER_COUNT = 3

/**
 * The shape of a worker's months: steady at their score, climbing to it
 * from lower, falling to it from higher, or jumping about around it. Each
 * ends at the worker's own score, so the latest month agrees with the
 * roster's number.
 */
export type DemoArc = 'steady' | 'improving' | 'declining' | 'erratic'

const ARCS: readonly DemoArc[] = ['steady', 'improving', 'declining', 'erratic']

export interface DemoActiveWorker extends DemoWorker {
  /** How many months of outcomes the worker has, the current month included: 1 is this month only. */
  months: number
  arc: DemoArc
  /** The score the worker is behaving like in each of those months, oldest first - what decides each shift's outcome. */
  monthlyScores: readonly number[]
}

/** An operator login the demo creates beside the contact's: the name, and the part of the login email before the @. */
export interface DemoOperator {
  emailName: string
  firstName: string
  lastName: string
}

export interface DemoPlan {
  size: DemoSize
  monthSpan: number
  facilities: readonly DemoFacility[]
  /**
   * The facilities that get activity - open shifts, a ranked pool, a score
   * run and a worker's login: every facility of the plan except in a large
   * demo, where the facilities added for scale have a roster and nothing
   * else. Closed months need no such list: they follow each facility's
   * outcomes.
   */
  activeFacilities: readonly DemoFacility[]
  /** The fixed roster rows for those facilities (the training cast, when included, is added by the CSV builder). */
  workers: readonly DemoWorker[]
  /** The workers who get reported activity, with how far back it goes. Every worker in the plan, so every trend has something in it. */
  activeWorkers: readonly DemoActiveWorker[]
  /** Whether Harbor View and its six scripted workers are part of the roster, with last month closed. */
  includesTrainingCast: boolean
  /** The operator logins created alongside the contact's: none, the standard demo's second operator, or enough to make a large demo's ten. */
  extraOperators: readonly DemoOperator[]
  /** A custom behavior, a changed master and one facility with its own settings, plus the custom behavior events. */
  hasBehaviors: boolean
  /** The facility given its own behavior settings: null where there is none, or only one facility with activity to compare it with. */
  behaviorOverrideFacilityId: string | null
  /** The facility set up with partner prioritization switched off, if any. */
  unprioritizedFacilityId: string | null
  /** The workers who get their own sign-in to the worker app: a name and facility each, so the demo can show the worker's seat. */
  workerLogins: readonly DemoWorkerLogin[]
  /**
   * The facilities every add-on is switched on at. A demo client holds a
   * demo license for every add-on from activation; the switch is the demo
   * step's. One active facility is left off where there is more than one,
   * so the demo shows the gate as well as the feature.
   */
  addOnFacilities: readonly DemoFacility[]
  /** Each active facility's story (plan H4): the one chosen everywhere, except a large demo deals them one of each in turn. */
  scenarios: Readonly<Record<string, DemoScenario>>
  /** The units every facility of the demo schedules on: the standard two, or a custom demo's. */
  units: readonly DemoUnitRecord[]
  /** Months the history publishes after this one. */
  monthsAhead: number
}

/** Every active facility but the second, where there are several: the first stays on, being the one every size shares. */
function addOnFacilitiesFor(activeFacilities: readonly DemoFacility[]): DemoFacility[] {
  return activeFacilities.length > 1 ? activeFacilities.filter((_, index) => index !== 1) : [...activeFacilities]
}

export interface DemoWorkerLogin {
  workerId: string
  facilityId: string
  name: string
}

/** The first worker of each facility in the plan, plus the training cast's Devon when the cast is included. */
function workerLoginsFor(facilities: readonly DemoFacility[], workers: readonly DemoWorker[], includesTrainingCast: boolean): DemoWorkerLogin[] {
  const firstPerFacility = facilities.flatMap((facility) => {
    const first = workers.find((worker) => worker.facilityId === facility.facilityId)
    return first ? [{ workerId: first.workerId, facilityId: first.facilityId, name: first.name }] : []
  })
  const cast = includesTrainingCast ? trainingWorkerNamed(TRAINING_WORKER_LOGIN_ID) : null
  return cast ? [...firstPerFacility, cast] : firstPerFacility
}

// ---- Seeded variety ----

/** A fixed seed: the same demo every time, by design. */
const PLAN_SEED = 0x52454c49

/** mulberry32: a small, good-enough generator for picking spans and arcs. Returns the next value in [0, 1). */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(random: () => number, from: readonly T[]): T {
  const item = from[Math.floor(random() * from.length)]
  if (item === undefined) {
    throw new Error('Nothing to pick from')
  }
  return item
}

/** A shuffled copy, each item inserted at a random position, leaving the input as it was. */
function shuffled<T>(random: () => number, items: readonly T[]): T[] {
  return items.reduce<T[]>((copy, item) => {
    const at = Math.floor(random() * (copy.length + 1))
    return [...copy.slice(0, at), item, ...copy.slice(at)]
  }, [])
}

const SCORE_FLOOR = 10
const SCORE_CEILING = 100
const IMPROVING_CLIMB = 35
const DECLINING_FALL = 30
const ERRATIC_SWING = 20

const clampScore = (value: number): number => Math.min(SCORE_CEILING, Math.max(SCORE_FLOOR, Math.round(value)))

/** The score a worker behaves like in each month of their span, oldest first, ending at their own score. */
function monthlyScoresFor(random: () => number, score: number, months: number, arc: DemoArc): number[] {
  const progress = (month: number): number => (months === 1 ? 1 : month / (months - 1))
  return Array.from({ length: months }, (_, month) => {
    switch (arc) {
      case 'steady':
        return score
      case 'improving':
        return clampScore(score - IMPROVING_CLIMB * (1 - progress(month)))
      case 'declining':
        return clampScore(score + DECLINING_FALL * (1 - progress(month)))
      case 'erratic':
        return month === months - 1 ? score : clampScore(score + (random() * 2 - 1) * ERRATIC_SWING)
    }
  })
}

/** The fewest months a shorter span gets when the whole span allows it, so even a partial trend has two points. */
const SHORTER_SPAN_FLOOR = 2

/** A span shorter than the whole, so the worker's trend starts partway: from a couple of months up to one month short of the span. */
function shorterSpan(random: () => number, monthSpan: number): number {
  const lowest = Math.min(SHORTER_SPAN_FLOOR, monthSpan - 1)
  return monthSpan === 1 ? 1 : lowest + Math.floor(random() * (monthSpan - lowest))
}

function activate(random: () => number, worker: DemoWorker, months: number, arc: DemoArc): DemoActiveWorker {
  return { ...worker, months, arc, monthlyScores: monthlyScoresFor(random, worker.score, months, arc) }
}

/**
 * Who gets the whole span and who gets less: the workers in `fullSpan` get
 * every month, the rest a shorter, seeded span. Arcs are seeded too,
 * except that the roster's first full-span worker always improves - the
 * most telling trend to open a demo on. Returned in roster order.
 */
function activateWorkers(random: () => number, workers: readonly DemoWorker[], monthSpan: number, fullSpan: ReadonlySet<string>, scenarioOf: (facilityId: string) => DemoScenario = () => DEFAULT_DEMO_SCENARIO): DemoActiveWorker[] {
  const lead = workers.find((worker) => fullSpan.has(worker.workerId))?.workerId
  const arcOf = (worker: DemoWorker): DemoArc => arcFor(random, scenarioOf(worker.facilityId))
  return workers.map((worker) => {
    if (fullSpan.has(worker.workerId)) {
      return activate(random, worker, monthSpan, worker.workerId === lead ? 'improving' : arcOf(worker))
    }
    return activate(random, worker, shorterSpan(random, monthSpan), arcOf(worker))
  })
}

/**
 * A worker's arc under the facility's story (plan H4): a turnaround is mostly improving, a struggling facility
 * mostly declining or erratic; every other story draws from all four alike, exactly as before scenarios existed,
 * so a steady demo's people are the same as ever.
 */
function arcFor(random: () => number, scenario: DemoScenario): DemoArc {
  if (scenario === 'turnaround') {
    return random() < 0.75 ? 'improving' : pick(random, ARCS)
  }
  if (scenario === 'struggling') {
    const roll = random()
    return roll < 0.55 ? 'declining' : roll < 0.8 ? 'erratic' : pick(random, ARCS)
  }
  return pick(random, ARCS)
}

/** The same story at every facility. */
const everywhere = (facilities: readonly DemoFacility[], scenario: DemoScenario): Record<string, DemoScenario> => Object.fromEntries(facilities.map((facility) => [facility.facilityId, scenario]))

/** At least half the roster, chosen by a seeded shuffle so the full-span workers are spread across facilities and tiers. */
function halfTheRoster(random: () => number, workers: readonly DemoWorker[]): ReadonlySet<string> {
  return new Set(shuffled(random, workers.map((worker) => worker.workerId)).slice(0, Math.ceil(workers.length / 2)))
}

// ---- The plans ----

/** Nothing to set up: no facilities, no workers, no activity. The month span is carried but unused. */
function blankPlan(monthSpan: number): DemoPlan {
  return {
    size: 'blank',
    monthSpan,
    facilities: [],
    activeFacilities: [],
    addOnFacilities: [],
    workers: [],
    activeWorkers: [],
    includesTrainingCast: false,
    extraOperators: [],
    hasBehaviors: false,
    behaviorOverrideFacilityId: null,
    unprioritizedFacilityId: null,
    workerLogins: [],
    scenarios: {},
    units: DEMO_UNIT_RECORDS,
    monthsAhead: DEFAULT_CUSTOM_DEMO.monthsAhead,
  }
}

function minimumPlan(monthSpan: number, scenario: DemoScenario): DemoPlan {
  const facilities = DEMO_FACILITIES.filter((facility) => facility.facilityId === MINIMUM_FACILITY_ID)
  const named = DEMO_WORKERS.filter((worker) => worker.facilityId === MINIMUM_FACILITY_ID).slice(0, MINIMUM_WORKER_COUNT)
  const scenarios = everywhere(facilities, scenario)
  // The three named workers, then the staff a working facility of its size needs (demo history plan H3).
  const staff = staffingFillFor(facilities, named, monthSpan, (facilityId) => scenarios[facilityId] ?? DEFAULT_DEMO_SCENARIO)
  const workers = [...named, ...staff]
  return {
    size: 'minimum',
    monthSpan,
    facilities,
    activeFacilities: facilities,
    workers,
    // The first worker - the gold - with the whole span, so there is one complete trend, climbing into gold; the staff have it all.
    activeWorkers: activateWorkers(seededRandom(PLAN_SEED), workers, monthSpan, new Set([...named.slice(0, 1), ...staff].map((worker) => worker.workerId)), () => scenario),
    includesTrainingCast: false,
    extraOperators: [],
    hasBehaviors: false,
    behaviorOverrideFacilityId: null,
    unprioritizedFacilityId: null,
    workerLogins: workerLoginsFor(facilities, workers, false),
    addOnFacilities: addOnFacilitiesFor(facilities),
    scenarios,
    units: DEMO_UNIT_RECORDS,
    monthsAhead: DEFAULT_CUSTOM_DEMO.monthsAhead,
  }
}

/** Every standard facility's staff beyond the fixed roster (demo history plan H3): what a working facility of its size employs, with the turnover of the span. */
function standardStaff(monthSpan: number, scenarioOf: (facilityId: string) => DemoScenario): DemoWorker[] {
  return staffingFillFor(DEMO_FACILITIES, DEMO_WORKERS, monthSpan, scenarioOf)
}

function standardActiveWorkers(monthSpan: number, staff: readonly DemoWorker[], scenarioOf: (facilityId: string) => DemoScenario): DemoActiveWorker[] {
  const random = seededRandom(PLAN_SEED)
  // At least half the fixed roster with the whole span, as before; the added staff are there every month (their hire and last day decide when they work).
  const fullSpan = new Set([...halfTheRoster(random, DEMO_WORKERS), ...staff.map((worker) => worker.workerId)])
  return activateWorkers(random, [...DEMO_WORKERS, ...staff], monthSpan, fullSpan, scenarioOf)
}

/** The standard demo's second operator, whose login is "operator@" the demo's domain. */
function secondOperator(): DemoOperator {
  return { emailName: 'operator', ...DEMO_SECOND_OPERATOR }
}

function standardPlan(monthSpan: number, scenarios: Readonly<Record<string, DemoScenario>>): DemoPlan {
  const scenarioOf = (facilityId: string): DemoScenario => scenarios[facilityId] ?? DEFAULT_DEMO_SCENARIO
  const staff = standardStaff(monthSpan, scenarioOf)
  const workers = [...DEMO_WORKERS, ...staff]
  return {
    size: 'standard',
    monthSpan,
    facilities: DEMO_FACILITIES,
    activeFacilities: DEMO_FACILITIES,
    workers,
    // At least half the roster with the whole span.
    activeWorkers: standardActiveWorkers(monthSpan, staff, scenarioOf),
    includesTrainingCast: true,
    extraOperators: [secondOperator()],
    hasBehaviors: true,
    behaviorOverrideFacilityId: DEMO_BEHAVIOR_OVERRIDES.facilityId,
    unprioritizedFacilityId: DEMO_UNPRIORITIZED_FACILITY_ID,
    workerLogins: workerLoginsFor(DEMO_FACILITIES, workers, true),
    addOnFacilities: addOnFacilitiesFor(DEMO_FACILITIES),
    scenarios: Object.fromEntries(DEMO_FACILITIES.map((facility) => [facility.facilityId, scenarioOf(facility.facilityId)])),
    units: DEMO_UNIT_RECORDS,
    monthsAhead: DEFAULT_CUSTOM_DEMO.monthsAhead,
  }
}

/**
 * The standard plan with more around it: the same facilities, workers and
 * activity, then the extra facilities and their rosters, and operators up
 * to the total (the contact's login and the second operator count towards
 * it). Only the standard facilities are active.
 */
function largePlan(monthSpan: number, scenario: DemoScenario): DemoPlan {
  const extraFacilities = largeExtraFacilities(DEMO_FACILITIES.length)
  // Twelve facilities with history (plan D5): the standard four and the first eight added for scale, each dealt a story in turn from the one chosen.
  const activeExtras = extraFacilities.slice(0, LARGE_ACTIVE_EXTRA_FACILITIES)
  const start = SCENARIO_ROTATION.indexOf(scenario)
  const scenarios: Record<string, DemoScenario> = Object.fromEntries([...DEMO_FACILITIES, ...activeExtras].map((facility, index) => [facility.facilityId, SCENARIO_ROTATION[(start + index) % SCENARIO_ROTATION.length] ?? scenario]))
  const scenarioOf = (facilityId: string): DemoScenario => scenarios[facilityId] ?? DEFAULT_DEMO_SCENARIO
  const standard = standardPlan(monthSpan, scenarios)
  const extraWorkers = largeExtraWorkers(extraFacilities)
  const extraNamed = extraWorkers.filter((worker) => activeExtras.some((facility) => facility.facilityId === worker.facilityId))
  // Numbered after the standard four, so their staff's names are their own.
  const extraStaff = staffingFillFor(activeExtras, extraNamed, monthSpan, scenarioOf, DEMO_FACILITIES.length)
  const extraActive = [...extraNamed, ...extraStaff]
  const extraActiveWorkers = activateWorkers(seededRandom(PLAN_SEED + 1), extraActive, monthSpan, new Set(extraActive.map((worker) => worker.workerId)), scenarioOf)
  const activeFacilities = [...standard.activeFacilities, ...activeExtras]
  const furtherOperators = largeExtraOperators(LARGE_OPERATOR_TOTAL - 1 - standard.extraOperators.length).map((operator) => ({
    emailName: `${operator.firstName}.${operator.lastName}`.toLowerCase(),
    ...operator,
  }))
  return {
    ...standard,
    size: 'large',
    facilities: [...standard.facilities, ...extraFacilities],
    activeFacilities,
    workers: [...standard.workers, ...extraWorkers, ...extraStaff],
    activeWorkers: [...standard.activeWorkers, ...extraActiveWorkers],
    extraOperators: [...standard.extraOperators, ...furtherOperators],
    addOnFacilities: addOnFacilitiesFor(activeFacilities),
    scenarios,
  }
}

/**
 * A custom demo's facility on its own behavior settings: the standard one when it has activity, else the last facility
 * with activity, and none with only one - the facility it would be compared with has to exist (the behaviors step
 * failed with "Facility not found" for a one-facility custom demo, 25 October 2026).
 */
function customOverrideFacility(active: readonly DemoFacility[]): string | null {
  if (active.length < 2) {
    return null
  }
  return active.some((facility) => facility.facilityId === DEMO_BEHAVIOR_OVERRIDES.facilityId) ? DEMO_BEHAVIOR_OVERRIDES.facilityId : (active[active.length - 1]?.facilityId ?? null)
}

/**
 * The demo the admin built on the form (custom): the facilities with history, the first three standard ones and then
 * those added for scale, each with its units, its named workers and the staff its plan needs at the share set; any
 * roster-only facilities after them; the history and months ahead as set. No training cast - its scripted months
 * belong to the standard demo.
 */
function customPlan(monthSpan: number, scenario: DemoScenario, settings: CustomDemoSettings): DemoPlan {
  const standardActive = DEMO_FACILITIES.filter((facility) => facility.facilityId !== TRAINING_FACILITY.facilityId)
  // Beyond the three standard facilities, those added for scale: first the ones with history, then the roster-only ones.
  const extrasWithHistory = Math.max(0, settings.facilities - standardActive.length)
  const extras = largeExtraFacilities(DEMO_FACILITIES.length).slice(0, extrasWithHistory + settings.rosterOnlyFacilities)
  const active = [...standardActive.slice(0, settings.facilities), ...extras.slice(0, extrasWithHistory)]
  const rosterOnly = extras.slice(extrasWithHistory)
  const units = customUnits(settings)
  const targets = staffingTargetsFor(units, settings.staffingPct)
  const scenarios = everywhere(active, scenario)
  const scenarioOf = (facilityId: string): DemoScenario => scenarios[facilityId] ?? DEFAULT_DEMO_SCENARIO
  const extraNamed = largeExtraWorkers(extras)
  const named = [...DEMO_WORKERS.filter((worker) => active.some((facility) => facility.facilityId === worker.facilityId)), ...extraNamed.filter((worker) => active.some((facility) => facility.facilityId === worker.facilityId))]
  const staff = staffingFillFor(active, named, monthSpan, scenarioOf, 0, targets)
  const activeRoster = [...named, ...staff]
  const random = seededRandom(PLAN_SEED + 2)
  // Half the named workers with the whole span, as a standard demo has; the added staff every month.
  const fullSpan = new Set([...halfTheRoster(random, named), ...staff.map((worker) => worker.workerId)])
  return {
    size: 'custom',
    monthSpan,
    facilities: [...active, ...rosterOnly],
    activeFacilities: active,
    workers: [...activeRoster, ...extraNamed.filter((worker) => rosterOnly.some((facility) => facility.facilityId === worker.facilityId))],
    activeWorkers: activateWorkers(random, activeRoster, monthSpan, fullSpan, scenarioOf),
    includesTrainingCast: false,
    extraOperators: [secondOperator()],
    hasBehaviors: true,
    behaviorOverrideFacilityId: customOverrideFacility(active),
    unprioritizedFacilityId: active.some((facility) => facility.facilityId === DEMO_UNPRIORITIZED_FACILITY_ID) ? DEMO_UNPRIORITIZED_FACILITY_ID : null,
    workerLogins: workerLoginsFor(active, activeRoster, false),
    addOnFacilities: addOnFacilitiesFor(active),
    scenarios,
    units,
    monthsAhead: settings.monthsAhead,
  }
}

export function demoPlanFor({ size, monthSpan, scenario = DEFAULT_DEMO_SCENARIO, custom = DEFAULT_CUSTOM_DEMO }: DemoChoice): DemoPlan {
  if (size === 'custom') {
    const problems = [...customDemoProblems(custom, monthSpan), ...(isValidMonthSpan(monthSpan) ? [] : [`Months of history: a whole number from ${MONTH_SPAN_LIMITS.min} to ${customMaxMonthSpan(custom)}`])]
    if (problems.length > 0) {
      throw new Error(`The custom demo is out of range: ${problems.join('; ')}`)
    }
  } else if (!isValidMonthSpan(monthSpan, size)) {
    throw new Error(`The month span must be a whole number from ${MONTH_SPAN_LIMITS.min} to ${maxMonthSpanFor(size)}, not ${monthSpan}`)
  }
  switch (size) {
    case 'blank':
      return blankPlan(monthSpan)
    case 'minimum':
      return minimumPlan(monthSpan, scenario)
    case 'standard':
      return standardPlan(monthSpan, everywhere(DEMO_FACILITIES, scenario))
    case 'large':
      return largePlan(monthSpan, scenario)
    case 'custom':
      return customPlan(monthSpan, scenario, custom)
  }
}

// ---- Descriptions ----

/** How many workers the plan's roster import creates, training cast included. */
export function demoWorkerCount(plan: DemoPlan): number {
  return plan.workers.length + (plan.includesTrainingCast ? TRAINING_WORKER_IDS.length : 0)
}

/** How many workers have outcomes for the whole span. */
export function fullSpanWorkerCount(plan: DemoPlan): number {
  return plan.activeWorkers.filter((worker) => worker.months === plan.monthSpan).length
}

/**
 * How many earlier months the demo closes, so each has a final: every month
 * of the span but the current one - and never fewer than the one the
 * training cast's story needs closed.
 */
/**
 * The worker whose performance summary the demo generates: the first on
 * the first facility with the add-ons on, once the span has at least two
 * closed months to summarise. Null for a shorter span or a blank demo.
 */
export function demoSummaryWorker(plan: DemoPlan): DemoWorker | null {
  if (monthsToClose(plan) < 2) {
    return null
  }
  const facility = plan.addOnFacilities[0]
  return facility ? (plan.workers.find((worker) => worker.facilityId === facility.facilityId) ?? null) : null
}

/** The facility whose summaries the demo exports for HR, so the receipts list has an entry: the next facility with the add-ons on. */
export function demoExportFacilityId(plan: DemoPlan): string | null {
  const worker = demoSummaryWorker(plan)
  return worker ? (plan.addOnFacilities.find((facility) => facility.facilityId !== worker.facilityId)?.facilityId ?? null) : null
}

function monthsPhrase(months: number): string {
  return months === 1 ? 'this month’s' : `${months} months of`
}

/** One sentence of what the plan sets up, for the admin choosing a size. */
export function describeDemoPlan(plan: DemoPlan): string {
  if (isBlankPlan(plan)) {
    return 'Only the client itself: its registration, API key and operator login. Add facilities, workers and logins by hand afterwards from the admin pages.'
  }
  const facilityCount = plan.facilities.length
  // In a large demo the activity belongs to a few of the facilities, so the parts about it say which.
  const activeCount = plan.activeFacilities.length
  const scoped = activeCount < facilityCount
  const facilities = facilityCount === 1 ? `one facility (${plan.facilities[0]?.name}) with the default configuration and an administrator` : `${facilityCount} facilities with the default configuration and an administrator each`
  const at = scoped ? ` at ${activeCount} of the facilities` : ''
  // The history (demo history plans H1-H3): every month of the span lived at each facility with activity.
  const history = `${monthsPhrase(plan.monthSpan)} of history${at} - census, seats published from the staffing plan, bid, built, released and worked, call-outs covered and gaps run short${plan.monthSpan > 1 ? ', each earlier month closed' : ''}`
  const parts = [
    ...(plan.extraOperators.length === 1 ? ['a second operator'] : plan.extraOperators.length > 1 ? [`${plan.extraOperators.length + 1} operators in all`] : []),
    facilities,
    `${demoWorkerCount(plan)} workers, each facility with activity staffed for its ${plan.units.length === 1 ? 'unit' : `${plan.units.length} units`}`,
    ...(plan.hasBehaviors ? [plan.behaviorOverrideFacilityId ? 'a custom behavior with one facility on its own settings' : 'a custom behavior'] : []),
    history,
    ...(plan.includesTrainingCast ? [`two scripted months for the ${TRAINING_WORKER_IDS.length} at ${TRAINING_FACILITY.name}`] : []),
    'partner rankings',
  ]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}.`
}

/** The sizes as the onboarding form offers them, in display order; the first is the default. */
export const DEMO_SIZE_OPTIONS: readonly { size: DemoSize; label: string; summary: string }[] = [
  { size: 'blank', label: 'Blank', summary: 'No data at all, for building a demo up by hand.' },
  { size: 'minimum', label: 'Minimum', summary: 'One facility with its history; behaviors stay at their defaults, and the month taking bids is left for you to bid, build and release.' },
  { size: 'standard', label: 'Standard', summary: 'The full dataset, including the training materials’ facility and cast.' },
  {
    size: 'large',
    label: 'Large',
    summary: 'The standard dataset at a large operator’s scale: twelve facilities with history, each telling a different story, and the rest with a roster but no activity. Up to a year of history. Takes several minutes.',
  },
  {
    size: 'custom',
    label: 'Custom',
    summary: 'You set the facilities, units, beds, occupancy, staffing, history and months ahead, each within a realistic range. No training cast.',
  },
]
